// This file contains functions that do calculations or changes, etc
// related to state, but not thightly coupled to the full slice state,
// because it produced circle dependencies that broke the configurator.

import { conditionApplies } from './conditions';
import { calculateImplicitValuesByGroup, DefaultsSet } from './configurator-slice';
import { CatalogRoot } from './types/catalog-internal';
import { CfgProperty, Part, PropertyDefaultCase } from './types/catalog-types';
import { Composition, GroupItem, MAIN_SCENE, OptionValues } from './types/composition';

// ==== Counters ====

export type MeshID = {
    elementId: string
    partId: string
    meshName: string
}

export type ShownCounterMeshes = Record<string,MeshID[]>;

export function calculateCountersUse(composition: Composition, 
                                     catalog: CatalogRoot,
                                     implicitValuesByGroup: Record<string,OptionValues>): {
    maxShowablePerCounter: Record<string,number>
    maxShowablePerCounterGroup: Record<string,Record<string,number>>
    shownMeshesPerGroup: ShownCounterMeshes
} {
    const maxShowablePerCounter: Record<string,number> = {};
    const maxShowablePerCounterGroup: Record<string,Record<string,number>> = {}
    const shownMeshesPerGroup: ShownCounterMeshes = {};

    for(const groupItem of composition.groups) {

        const groupShowableMeshes: ShownCounterMeshes = {};
        const implicitValues = implicitValuesByGroup[groupItem.id];

        for(const element of groupItem.elements) {

            let parts = getPartsOfElement(element.element, catalog, implicitValues);

            for(const part of parts) {

                for(const meshRule of (part.meshes ?? [])) {

                    const counter = meshRule.counter;

                    if(counter) {

                        // global count per counter
                        if(!maxShowablePerCounter[counter]) {
                            maxShowablePerCounter[counter] = 1;
                        } else {
                            maxShowablePerCounter[counter] += 1;
                        }

                        
                        // count in this group
                        if(!maxShowablePerCounterGroup[counter]) {
                            maxShowablePerCounterGroup[counter] = {}
                        }
                        if(!maxShowablePerCounterGroup[counter][groupItem.id]) {
                            maxShowablePerCounterGroup[counter][groupItem.id] = 1;
                        } else {
                            maxShowablePerCounterGroup[counter][groupItem.id] += 1;
                        }

                        const meshID: MeshID = {
                            elementId: element.id,
                            partId: part.id,
                            meshName: meshRule.test
                        }

                        // mesh list in this group
                        if(!groupShowableMeshes[counter]) {
                            groupShowableMeshes[counter] = [meshID]
                        } else {
                            groupShowableMeshes[counter].push(meshID)
                        }

                    }

                }

            }

        }

        // TODO "shuffle" groupShowableMeshes in a repeatible way 
        // to make it appear more distributed

        if(groupItem.counters) {

            for(const [ counter, amount ] of Object.entries(groupItem.counters)) {

                const toAdd = groupShowableMeshes[counter] ? groupShowableMeshes[counter].slice(0, amount) : [];

                if(!shownMeshesPerGroup[counter]) {
                    shownMeshesPerGroup[counter] = toAdd;
                } else {
                    shownMeshesPerGroup[counter].push(...toAdd);
                }

            }

        }

    }

    return { maxShowablePerCounter, maxShowablePerCounterGroup, shownMeshesPerGroup };
}

function getPartsOfElement(elementId: string, catalog: CatalogRoot, implicitValues: OptionValues): Part[] {

    const parts = [];

    for(const elementPart of catalog.elementParts) {        
        if(elementPart.element === elementId && conditionApplies(elementPart.showif, implicitValues, catalog.qualities, [])) {
            for(const part of catalog.parts) {
                if(part.id === elementPart.part) {
                    parts.push(part);
                }
            }
        }
    }

    return parts;
}


/**
 * Adjusts the number of counter dependent meshes per group.
 * 
 * - Corrects the number shown in a group in case it is too much
 *   (element deletion in groups).
 * - If there are too much elements removes them from the scene
 * - If there are too few elements adds them to the scene
 * 
 * This function is to be used after adding elements or deleting elements.
 * 
 */

export function normalizeCounters(composition: Composition, catalog: CatalogRoot, defaults: DefaultsSet) {

    // only makes sense if there's content, and without groups, there's no counter
    if(composition.groups.length === 0) {
        return;
    }

    const implicitValuesByGroup = calculateImplicitValuesByGroup(composition, defaults, catalog.qualities);
    const { maxShowablePerCounter, maxShowablePerCounterGroup } = calculateCountersUse(composition, catalog, implicitValuesByGroup);
    const counterNames = Object.keys(maxShowablePerCounter);
    
    // first remove excess from groups
    for(const counterName of counterNames) {

        const maxPerGroup = maxShowablePerCounterGroup[counterName];

        for(const group of composition.groups) {
            if(maxPerGroup[group.id] !== undefined && group.counters && // preconditions
               (group.counters[counterName] ?? 0) > maxPerGroup[group.id]) {
                group.counters[counterName] = maxPerGroup[group.id];
            }

        }
    }
    
    const count = countCounterMeshesShown(composition.groups, counterNames);
    for(const [counterName, currentShown] of Object.entries(count)) {

        const groupWithProperty = composition.groups.find(group => group.options[counterName] !== undefined);
        if(!groupWithProperty) continue;
        const referenceValues = groupWithProperty.options;                

        const currentValue = Number.parseInt(referenceValues[counterName] ?? '0');
        const toBeShown = Math.min(currentValue, maxShowablePerCounter[counterName]);
        if(currentShown != toBeShown) {
            const maxPerGroup = maxShowablePerCounterGroup[counterName];
            spreadCounterChange(composition.groups, counterName, toBeShown - currentShown, maxPerGroup);
        }
        
    }


}


/**
 * Adds or removes (depending on sign of diff) the per-group counters of a
 * global counter between the remaining groups.
 * @param groups Groups to spread the change into
 * @param counterName Name of the counter
 * @param diff Amount to change
 * @param maxPerGroup How much meshes of that counter can show each group
 */
export function spreadCounterChange(groups: GroupItem[], counterName: string, diff: number, maxPerGroup: Record<string,number>) {

    const groupsWithCounter = groups.filter(g => maxPerGroup[g.id] !== undefined);

    let stop = false;
    while(diff !== 0 && !stop) {

        stop = true;
        if(diff > 0) {

            let chosenGroup;
            let chosenAmount = Number.MAX_SAFE_INTEGER;

            for(const group of groupsWithCounter) {

                const groupAmount = group.counters ? (group.counters[counterName] ?? 0) : 0;

                if(groupAmount < maxPerGroup[group.id]) {
                    // it is suitable, and at least one it is, so... don't stop yet
                    stop = false;

                    if(groupAmount < chosenAmount) {
                        chosenAmount = groupAmount;
                        chosenGroup = group;
                    }
                }
            }

            if(chosenGroup) {
                if(!chosenGroup.counters) {
                    chosenGroup.counters = {
                        [counterName]: 1
                    };
                } else if(!chosenGroup.counters[counterName]) {
                    chosenGroup.counters[counterName] = 1;
                } else {
                    chosenGroup.counters[counterName] += 1;
                }
            }

            diff -= 1;

        } else {

            let chosenGroup;
            let chosenAmount = Number.MIN_SAFE_INTEGER;

            for(const group of groupsWithCounter) {

                const groupAmount = group.counters ? (group.counters[counterName] ?? 0) : 0;

                if(groupAmount > 0) {
                    // it is suitable, and at least one it is, so... don't stop yet
                    stop = false;

                    if(groupAmount > chosenAmount) {
                        chosenAmount = groupAmount;
                        chosenGroup = group;
                    }
                }

            }

            if(chosenGroup) {
                chosenGroup.counters![counterName]! -= 1;
            }

            diff += 1;
            
        }
    }

}


/**
 * Counts, per counter, the number of meshes shown
 * @param groups
 * @param counterNames
 * @returns Number of shown meshes per counter
 */
function countCounterMeshesShown(groups: GroupItem[], counterNames: string[]) {
    
    const count: Record<string,number> = Object.fromEntries(counterNames.map(n => [n,0]));

    for(const group of groups) {
        if(group.counters) {
            for(const [counterName, value] of Object.entries(group.counters)) {
                count[counterName] += value;
            }
        }
    }

    return count;
}


// ==== Options and values ====

export function calculateInitialsAndDefaults(properties: CfgProperty[]): [
    Record<string,OptionValues>,
    Record<string, string|null|PropertyDefaultCase[]>
] {
    const values: Record<string,OptionValues> = {};
    const defaults: Record<string, string|null|PropertyDefaultCase[]> = {};
    for(const p of properties) {
        
        let value: string|null = null;
        if(p.initial !== undefined) {
            value = p.initial;
        } else {
            value = null;
        }

        let appliedScenes: string[] = [ MAIN_SCENE ];
        if(p.scene !== undefined) {
            appliedScenes = Array.isArray(p.scene) ? p.scene : [ p.scene ];
        }
        for(const scene of appliedScenes) {
            if(values[scene] === undefined) {
                values[scene] = {};
            }
            values[scene][p.name] = value;
        }

        if(p.default !== undefined) {
            defaults[p.name] = p.default;
        }
    }
    return [ values, defaults ];
}

export function findGroupWithElement(groups: GroupItem[], elementId: string|undefined) {
    return groups.find(g => g.elements.findIndex(e => e.id === elementId) >= 0);
}